import React from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from './Avatar';
import StarRating from './StarRating';
import { useApp } from '../context/AppContext';

const SeatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((date - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

export default function RideCard({ ride, showDriver = true, compact = false }) {
  const navigate = useNavigate();
  const { getUserById } = useApp();
  const driver = getUserById(ride.driverId);

  const statusColor = {
    open: 'var(--green)', full: 'var(--yellow)', cancelled: 'var(--red)',
    completed: 'var(--gray-400)',
  }[ride.status] || 'var(--gray-400)';

  return (
    <div
      onClick={() => navigate(`/ride/${ride.id}`)}
      style={{
        background: 'var(--navy-light)',
        border: '1px solid var(--navy-border)',
        borderRadius: 'var(--radius)',
        padding: compact ? '14px 16px' : '16px',
        cursor: 'pointer',
        transition: 'border-color 0.2s, transform 0.15s',
        marginBottom: 12,
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--navy-border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Route */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
          <div style={{ paddingTop: 2 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', marginBottom: 4 }} />
            <div style={{ width: 2, height: 16, background: 'var(--navy-border)', margin: '0 auto', marginBottom: 4 }} />
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', marginBottom: 8, lineHeight: 1.3 }}>
              {ride.departure}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', lineHeight: 1.3 }}>
              {ride.destination}
            </div>
          </div>
        </div>
      </div>

      {/* Meta row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--gray-300)' }}>
          <ClockIcon /> {formatDate(ride.date)}, {formatTime(ride.time)}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--gray-300)' }}>
          <SeatIcon /> {ride.seatsAvailable} seat{ride.seatsAvailable !== 1 ? 's' : ''} left
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
          ${ride.price}/person
        </span>
      </div>

      {/* Driver row */}
      {showDriver && driver && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          paddingTop: 10, borderTop: '1px solid var(--navy-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar user={driver} size={30} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{driver.name}</div>
              <StarRating rating={driver.rating} size={11} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor }} />
            <span style={{ fontSize: 12, color: statusColor, fontWeight: 500, textTransform: 'capitalize' }}>
              {ride.status}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
